import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navItems = [
    { name: 'Home', path: '/' },
    { 
      name: 'About', 
      path: '/about',
      megaMenu: {
        title: 'Who We Are',
        items: [
          { name: 'Our Story', path: '/about', icon: 'FiBookOpen', desc: 'The history, mission, and philosophy of Boes & Boes.' },
          { name: 'Terry Boes', path: '/about/terry-boes', icon: 'FiUser', desc: 'Managing Partner & Founder' },
          { name: 'Linda Boes', path: '/about/linda-boes', icon: 'FiUser', desc: 'CEO & Talent Development Specialist' }
        ],
        featured: {
          title: 'Decades of Experience',
          image: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1785005856713-blob',
          link: '/about'
        }
      }
    },
    { 
      name: 'Services', 
      path: '/services',
      megaMenu: {
        title: 'Our Capabilities',
        items: [
          { name: 'Services Overview', path: '/services', icon: 'FiGrid', desc: 'See how our solutions integrate.' },
          { name: 'Emergency Planning', path: '/services/emergency-planning', icon: 'FiFileText', desc: 'EAPs, ERPs, BCPs, and EOC procedures.' },
          { name: 'Compliance Consulting', path: '/services/compliance-consulting', icon: 'FiShield', desc: 'OSHA, EPA, and NFPA alignment.' },
          { name: 'Drills & Exercises', path: '/services/drills-exercises', icon: 'FiActivity', desc: 'HSEEP-aligned drills and exercises.' },
          { name: 'Training & Development', path: '/services/training-development', icon: 'FiTarget', desc: 'Custom course development.' },
          { name: 'Leadership Development', path: '/services/leadership-development', icon: 'FiUsers', desc: 'Front-line academies and EQ training.' }
        ],
        featured: {
          title: 'A Connected System',
          image: 'https://media-manager-c.questera.ai/greta-media/e842e0d41a21d55020287eff0799ef371d19c38aa92b057bb3ca6b830a67458ee61195b657411aeb9534fd09dfbbea8f/images/aW1hZ2UvanBlZw==/6cc24e649b63ee959d555c70c4cad774.jpg',
          link: '/services'
        }
      }
    },
    { 
      name: 'Training', 
      path: '/training/catalog',
      megaMenu: {
        title: 'Instruction & Education',
        items: [
          { name: 'Course Catalog', path: '/training/catalog', icon: 'FiBook', desc: 'Browse all available training programs.' },
          { name: 'Upcoming Courses', path: '/training/upcoming', icon: 'FiCalendar', desc: 'Register for scheduled public courses.' },
          { name: 'ProBoard Pathways', path: '/training/catalog', icon: 'FiAward', desc: 'NFPA aligned certification tracks.' }
        ],
        featured: {
          title: 'Expert Instruction',
          image: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1785006145154-blob',
          link: '/training/catalog'
        }
      }
    },
    { name: 'Experience', path: '/experience' },
    { name: 'Industries', path: '/industries' },
    { name: 'Resources', path: '/resources' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <nav 
      className={`fixed top-0 w-full z-50 transition-all duration-300 bg-white ${isScrolled ? 'shadow-md py-2' : 'shadow-sm py-4 border-b border-slate-200'}`}
      onMouseLeave={() => setActiveDropdown(null)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex justify-between items-center">
          
          <Link to="/" className="flex items-center z-50" onClick={() => setActiveDropdown(null)}>
            <img 
              src="https://media-manager-c.questera.ai/greta-media/e842e0d41a21d55020287eff0799ef371d19c38aa92b057bb3ca6b830a67458ee61195b657411aeb9534fd09dfbbea8f/images/aW1hZ2UvcG5n/2eef99af68f71a4e5d50808daf0f01df.png" 
              alt="Boes & Boes Training Consultants" 
              className="h-14 md:h-16 w-auto object-contain drop-shadow-sm"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden xl:flex items-center space-x-2">
            {navItems.map((item) => (
              <div 
                key={item.name} 
                className="relative px-2 py-8 -my-8"
                onMouseEnter={() => setActiveDropdown(item.name)}
              >
                <Link 
                  to={item.path} 
                  className={`text-sm font-semibold transition-colors flex items-center gap-1 ${activeDropdown === item.name ? 'text-brand-green' : 'text-slate-700 hover:text-brand-navy'}`}
                  onClick={() => setActiveDropdown(null)}
                >
                  {item.name}
                  {item.megaMenu && <SafeIcon icon={FiIcons.FiChevronDown} className={`text-xs opacity-70 transition-transform duration-300 ${activeDropdown === item.name ? 'rotate-180' : ''}`} />}
                </Link>
              </div>
            ))}
            <div className="pl-4">
              <Link 
                to="/consultation" 
                className="px-5 py-2.5 rounded-lg text-sm font-bold bg-brand-green text-white hover:bg-green-600 transition-all shadow-md shadow-brand-green/20"
                onClick={() => setActiveDropdown(null)}
              >
                Free Consultation
              </Link>
            </div>
          </div>

          {/* Mobile Toggle */}
          <button 
            className="xl:hidden p-2 rounded text-slate-900 hover:bg-slate-100 transition-colors z-50"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <SafeIcon icon={isMobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="text-2xl" />
          </button>
        </div>

        {/* Mega Menu Overlay (Desktop) */}
        <AnimatePresence>
          {activeDropdown && navItems.find(i => i.name === activeDropdown)?.megaMenu && (
            <motion.div 
              initial={{ opacity: 0, y: 15, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="absolute top-full left-4 right-4 sm:left-6 sm:right-6 lg:left-8 lg:right-8 pt-4 z-40 hidden xl:block"
            >
              {(() => {
                const menuData = navItems.find(i => i.name === activeDropdown).megaMenu;
                return (
                  <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 p-8 flex gap-8 overflow-hidden relative">
                    {/* Top Accent Line */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-navy to-brand-green"></div>
                    
                    {/* Left: Link Grid */}
                    <div className="w-2/3">
                      <h3 className="text-xs font-bold text-brand-gray uppercase tracking-wider mb-6">
                        {menuData.title}
                      </h3>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                        {menuData.items.map((subItem, idx) => (
                          <Link 
                            key={idx} 
                            to={subItem.path} 
                            className="group/link flex items-start gap-4 p-3 -m-3 rounded-xl hover:bg-slate-50 transition-colors"
                            onClick={() => setActiveDropdown(null)}
                          >
                            <div className="mt-0.5 w-10 h-10 rounded-lg bg-brand-light flex items-center justify-center text-brand-navy group-hover/link:bg-brand-navy group-hover/link:text-white transition-colors shrink-0">
                              <SafeIcon icon={FiIcons[subItem.icon]} className="text-lg" />
                            </div>
                            <div>
                              <div className="font-bold text-slate-800 text-sm mb-1 group-hover/link:text-brand-green transition-colors">
                                {subItem.name}
                              </div>
                              <p className="text-xs text-slate-500 font-medium leading-relaxed">
                                {subItem.desc}
                              </p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Right: Featured Content */}
                    <div className="w-1/3 relative rounded-xl overflow-hidden group/featured">
                      <img 
                        src={menuData.featured.image} 
                        alt={menuData.featured.title} 
                        className="w-full h-full object-cover group-hover/featured:scale-105 transition-transform duration-700" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent opacity-90"></div>
                      <div className="absolute bottom-6 left-6 right-6">
                        <h4 className="text-white font-bold text-xl mb-2">{menuData.featured.title}</h4>
                        <Link 
                          to={menuData.featured.link} 
                          className="inline-flex items-center gap-2 text-sm font-bold text-brand-green hover:text-white transition-colors"
                          onClick={() => setActiveDropdown(null)}
                        >
                          Explore <SafeIcon icon={FiIcons.FiArrowRight} />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: '100vh' }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden absolute top-full left-0 w-full bg-white border-t border-slate-100 shadow-xl overflow-y-auto"
          >
            <div className="px-4 py-6 flex flex-col gap-2 pb-32">
              {navItems.map((item) => (
                <div key={item.name} className="flex flex-col">
                  <Link 
                    to={item.path} 
                    className="py-3 text-lg font-bold text-brand-navy border-b border-slate-100 flex justify-between items-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                    {item.megaMenu && <SafeIcon icon={FiIcons.FiChevronDown} className="text-sm text-slate-400" />}
                  </Link>
                  
                  {item.megaMenu && (
                    <div className="pl-4 flex flex-col border-b border-slate-100 py-2 bg-slate-50/50">
                      {item.megaMenu.items.map((dropItem) => (
                        <Link 
                          key={dropItem.name}
                          to={dropItem.path} 
                          className="py-2.5 text-base font-medium text-slate-600 hover:text-brand-navy flex items-center gap-3"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <SafeIcon icon={FiIcons[dropItem.icon]} className="text-brand-green shrink-0" />
                          {dropItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link 
                to="/consultation" 
                className="mt-6 text-center py-4 rounded-lg bg-brand-green text-white font-bold text-lg shadow-lg shadow-brand-green/20"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Schedule Free Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;